import { type PathParams } from "@constellation-academy/epic-ui";
import { BreadCrumb } from "primereact/breadcrumb";
import { useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";
import { useTranslation } from "@i18n/hooks";
import { type discountCodeScreen_Query } from "@relay/discountCodeScreen_Query.graphql";
import { QUERY } from "./discount-code.graphql";
import { type DiscountCodePath } from "./discount-code.paths";
import { DiscountCodeForm } from "./parts/DiscountCodeForm";
import { DiscountCodeUsageTable } from "./parts/DiscountCodeUsageTable";
import { DiscountUsageSearchContainer } from "./parts/DiscountUsageSearchContext";
import { BaseScreen } from "../../components/base-screen";
import { NoAccess } from "../../components/no-access";
import { useHasPermissions } from "../../hooks/use-has-permissions";
import { Paths } from "../../routes/paths";
import { type ActiveDiscountActionsPath } from "../discount-actions";

export const DiscountCodeScreen = () => {
	const { t } = useTranslation("common");

	const { discountActionId, discountCodeId } =
		useParams<PathParams<typeof ActiveDiscountActionsPath | typeof DiscountCodePath>>();

	const query = useLazyLoadQuery<discountCodeScreen_Query>(QUERY, {
		id: discountCodeId!,
	});

	const items: Array<{ label?: string; url: string }> = [
		{
			label: t("discounts.allActions"),
			url: Paths.activeDiscountActions.path,
		},
	];

	const canRead = useHasPermissions(["UserInAccountPermission_DiscountAdmin_Read"]);

	const canModify = useHasPermissions(["UserInAccountPermission_DiscountAdmin_Modify"]);

	if (canModify) {
		items.push({
			label: t("discounts.discountAction"),
			url: Paths.activeDiscountActions.withId(discountActionId!).edit.path,
		});

		items.push({
			label: query.node?.code,
			url: Paths.activeDiscountActions
				.withId(discountActionId!)
				.edit.discountCode.withId(discountCodeId!).path,
		});
	}

	if (!canRead) return <NoAccess />;
	if (!query.node) return <NoAccess />;

	return (
		<DiscountUsageSearchContainer>
			<BaseScreen
				title={t("discountCode.editTitle", {
					code: query.node.code,
				})}
			>
				<BreadCrumb className="mb-5" model={items} />
				{canModify && <DiscountCodeForm discountCodeFragmentRef={query.node} />}
				<DiscountCodeUsageTable query={query} />
			</BaseScreen>
		</DiscountUsageSearchContainer>
	);
};
